package frontend.modals

import AccountSelect
import AccountSelectByCurrency
import TransferTypeSelect
import androidx.compose.runtime.*
import net.sergeych.ugdx.shared.ApiException
import net.sergeych.ugdx.shared.Currency
import net.sergeych.ugdx.shared.Order
import net.sergeych.ugdx.shared.TransferOrder
import net.sergeych.ugdx.shared.api.ApiAccount
import net.sergeych.ugdx.shared.trader.TradeOrder
import net.sergeych.ugdx.shared.trader.Trader
import com.ionspin.kotlin.bignum.decimal.BigDecimal
import com.ionspin.kotlin.bignum.decimal.toBigDecimal
import components.*
import frontend.App
import frontend.components.Modal
import frontend.lib.classList
import frontend.lib.displayCurrencyCode
import frontend.models.User
import frontend.pages.*
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.InternalCoroutinesApi
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import kotlin.time.Duration.Companion.seconds

@Composable
fun ZAccountSelect(
    app: App,
    user: User,
    sellCurrency: Currency,
    sellAccount: ApiAccount?,
    selected: ApiAccount?,
    amount: BigDecimal?,
    rate: BigDecimal?,
    onChange: (account: ApiAccount?) -> Unit
) {
    val filtered = user.accounts.filter { it.currencyCode == sellCurrency.code }
    var freeBalance: BigDecimal? by remember { mutableStateOf(null) }

    @Composable
    fun BalanceInfo() {
        freeBalance?.let {
            val balance = it
            amount?.let {
                val remains = balance - it
                if (remains < BigDecimal.ZERO) Text("Insufficient funds")
                else Text("Remains ${d(remains, sellCurrency.roundingScale)} ${displayCurrencyCode(sellCurrency)}")
                Br()
            }
            Text("Available ${d(it, sellCurrency.roundingScale)} ${displayCurrencyCode(sellCurrency)}")
            Br()
            Text("tDUST/RY:ZYUZI rate: ${rate?.toPlainString()}")
            Br()
        }
    }

    LaunchedEffect(selected) {
        val account = sellAccount ?: selected
        account?.let {
            if (app.checkBalance) {
                if (freeBalance != null) freeBalance = null
                freeBalance = it.getAvailableBalance()
            } else freeBalance = it.balance
        }
    }

    if (filtered.size == 0) {

    } else if (filtered.size == 1) {
        selected?.let {
            val acc = it
            Span(attrs = { classList("text-xs") }) {
                BalanceInfo()
            }
        }
    } else {
        AccountSelectByCurrency(
            "acc",
            user,
            sellCurrency,
            selected
        ) {
            label { Text("Select ${displayCurrencyCode(sellCurrency)} account") }
            onChange { onChange(it) }
            condensed()
        }

        Div(attrs = { classList("mt-1 text-sm") }) {
            selected?.let {
                BalanceInfo()
            }
        }
    }
}


@Composable
fun BuyDUSTModal(app: App, user: User, zCurrency: Currency, dustAccount: ApiAccount, onClose: () -> Unit) {
    val scope = rememberCoroutineScope()
    var error: String? by mutableStateOf(null)
    var amount: BigDecimal? by remember { mutableStateOf(null) }
    var order: TradeOrder? by remember { mutableStateOf(null) }
    var zAccount: ApiAccount? by remember { mutableStateOf(null) }
    val dustCurrency = app.currencies.find { it.code == "tDUST" }
    var isLoading by remember { mutableStateOf(false) }

    var attempt by remember { mutableStateOf(0) }

    LaunchedEffect(true) {
        zAccount = getDefaultAccount(user, zCurrency) ?: user.createAccount(zCurrency,isDefault=true)
    }

    LaunchedEffect(attempt) {
        order?.let {
            val status = it.status
            if (
                status != TradeOrder.Status.EXECUTED &&
                status != TradeOrder.Status.REJECTED &&
                status != TradeOrder.Status.REMOVED
            ) {
                println("Refresh order")
                order = it.refresh()
                delay(5000)
                attempt += 1
            }
        }
    }

    fun stop(e: String) {
        if (e != "") error = e
        isLoading = false
    }

    fun buy(dustAccount: ApiAccount, zAccount: ApiAccount, amount: BigDecimal?) {
        if (amount == null || amount == BigDecimal.ZERO) return stop("Amount can't be empty")

        GlobalScope.launch {
            val balance = if (app.checkBalance) zAccount.getAvailableBalance() else zAccount.balance
            if (amount > balance) stop("Insufficient funds")
            else {
                val pairs = app.pairs
                val pair = pairs.find { it.base.code == "tDUST" && it.quote.code == zCurrency.code }

                try {
                    pair?.let {
                        order = Trader.createOrder(
                            it,
                            true,
                            amount,
                            BigDecimal.ONE,
                            null,
                            true,
                            dustAccount,
                            zAccount,
                            Clock.System.now() + 15.seconds
                        )
                        attempt += 1
                        isLoading = false
                    }
                } catch(e: ApiException) {
                    stop(e.text)
                }
            }
        }
    }

    Modal(onClose = { onClose() }) {
        if (order != null) {
            order?.let {
                H3(attrs = { classList("text-lg leading-6 font-medium text-gray-900") }) {
                    Text("Order created")
                }

                P {
                    Text("Order status: ${it.status}")
                }
            }
        }
        else Form(
            attrs = {
                classes("space-y-1")
                method(FormMethod.Post)
                action("javascript:void(0);")
            }
        ) {
            H3(attrs = { classList("text-lg leading-6 font-medium text-gray-900") }) {
                Text("Buy tDUST")
            }

            dustCurrency?.let {
                TDecimalInput("amount", amount) {
                    label { Text("Amount ${displayCurrencyCode(it)}") }
                    onChange { amount = it }
                    onInput { amount = it }
                    precision(it.roundingScale.toLong())
                }
            }

            zAccount?.let {
                ZAccountSelect(app, user, zCurrency, null, zAccount, amount, BigDecimal.ONE) {
                    zAccount = it
                }
            }

            if (error !== null) P(attrs = { classes("text-red-600") }) {
                Text(error ?: "")
            }

            zAccount?.let {
                TButton(attrs = {
                    onClick {
                        buy(dustAccount, it, amount)
                    }
                    isLoading(isLoading)
                }) {
                    Text("Buy")
                }
            }
        }
    }
}


@Composable
fun SellDUSTModal(app: App, user: User, zCurrency: Currency, dustAccount: ApiAccount, onClose: () -> Unit) {
    val scope = rememberCoroutineScope()
    var error: String? by mutableStateOf(null)
    var amount: BigDecimal? by remember { mutableStateOf(null) }
    var order: TradeOrder? by remember { mutableStateOf(null) }
    var zAccount: ApiAccount? by remember { mutableStateOf(null) }
    val dustCurrency = app.currencies.find { it.code == "tDUST" }
    var isLoading by remember { mutableStateOf(false) }

    var attempt by remember { mutableStateOf(0) }

    LaunchedEffect(true) {
        zAccount = getDefaultAccount(user, zCurrency) ?: user.createAccount(zCurrency)
    }

    LaunchedEffect(attempt) {
        order?.let {
            val status = it.status
            if (
                status != TradeOrder.Status.EXECUTED &&
                status != TradeOrder.Status.REJECTED &&
                status != TradeOrder.Status.REMOVED
            ) {
                println("Refresh order")
                order = it.refresh()
                delay(5000)
                attempt += 1
            }
        }
    }

    fun stop(e: String) {
        if (e != "") error = e
        isLoading = false
    }

    fun sell(dustAccount: ApiAccount, zAccount: ApiAccount, amount: BigDecimal?) {
        if (amount == null || amount == BigDecimal.ZERO) return stop("Amount can't be empty")

        GlobalScope.launch {
            val balance = if (app.checkBalance) dustAccount.getAvailableBalance() else dustAccount.balance
            if (amount > balance) stop("Insufficient funds")
            else {
                val pairs = app.pairs
                val pair = pairs.find { it.base.code == "tDUST" && it.quote.code == zCurrency.code }

                try {
                    pair?.let {
                        order = Trader.createOrder(
                            it,
                            false,
                            amount,
                            app.dustzaiBid ?: BigDecimal.ONE,
                            null,
                            true,
                            zAccount,
                            dustAccount,
                            Clock.System.now() + 15.seconds
                        )
                        attempt += 1
                        isLoading = false
                    }
                } catch(e: ApiException) {
                    stop(e.text)
                }
            }
        }
    }

    Modal(onClose = { onClose() }) {
        if (order != null) {
            order?.let {
                H3(attrs = { classList("text-lg leading-6 font-medium text-gray-900") }) {
                    Text("Order created")
                }

                P {
                    Text("Order status: ${it.status}")
                }
            }
        }
        else Form(
            attrs = {
                classes("space-y-1")
                method(FormMethod.Post)
                action("javascript:void(0);")
            }
        ) {
            H3(attrs = { classList("text-lg leading-6 font-medium text-gray-900") }) {
                Text("Sell tDUST")
            }

            dustCurrency?.let {
                TDecimalInput("amount", amount) {
                    label { Text("Amount ${displayCurrencyCode(it)}") }
                    onChange { amount = it }
                    onInput { amount = it }
                    precision(it.roundingScale.toLong())
                }
            }

            zAccount?.let {
                val zAcc = it
                dustCurrency?.let {
                    ZAccountSelect(app, user, it, dustAccount, zAcc, amount, app.dustzaiBid ?: BigDecimal.ONE) {
                        zAccount = it
                    }
                }
            }

            if (error !== null) P(attrs = { classes("text-red-600") }) {
                Text(error ?: "")
            }

            zAccount?.let {
                TButton(attrs = {
                    onClick {
                        sell(dustAccount, it, amount)
                    }
                    isLoading(isLoading)
                }) {
                    Text("Sell")
                }
            }
        }
    }
}
